import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Prescription } from "@/types";
import { Loader2, MessageCircle, Printer, X } from "lucide-react";
import { format } from "date-fns";
import { usePrescriptionsStore } from "@/lib/store";
import { useDoctorsStore } from "@/lib/stores/doctors";
import { formatPatientId } from '@/lib/utils/patientId';
import { formatPrescriptionId } from '@/lib/utils/prescriptionId';
import { formatMedicationDuration } from '@/lib/utils/dateUtils';
import { buildPrescriptionPrintHtml } from '@/lib/utils/prescriptionPrintHtml';
import { prescriptionPdfFilename, sharePrescriptionPdfDownloadAndWhatsApp } from '@/lib/utils/prescriptionPdf';
import { useHospitalProfileStore } from "@/lib/stores/hospitalProfile";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { whatsAppButtonClassName } from "@/components/shared/WhatsAppShareButton";

interface PrescriptionViewerDialogProps {
  prescription: Prescription | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  patientName: string;
  patientAge: number;
  patientGender: string;
  getDoctorName: (doctorId: string) => string;
  onPrint: () => void;
  doctors: { id: string; name: string; registrationNumber: string }[];
  patient?: any;
  patientContactNumber?: string | null;
}

export function PrescriptionViewerDialog({
  prescription,
  isOpen,
  onOpenChange,
  patientName,
  patientAge,
  patientGender,
  getDoctorName,
  onPrint,
  doctors,
  patient,
  patientContactNumber,
}: PrescriptionViewerDialogProps) {
  const { toast } = useToast();
  
  // Ensure all medications have the required frequency pattern fields
  const normalizedPrescription = prescription ? {
    ...prescription,
    medications: prescription.medications?.map(med => ({
      ...med,
      frequency: med.frequency || "",
      frequencyPattern: med.frequencyPattern || "",
      frequencyDescription: med.frequencyDescription || ""
    })) || []
  } : null;
  const { prescriptions } = usePrescriptionsStore();
  const { profile } = useHospitalProfileStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isSharingWhatsApp, setIsSharingWhatsApp] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Preview helper: render multiline text as bullets (big dots)
  const renderBulletLines = (text?: string) => {
    if (!text) return null;
    const lines = text
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l.length > 0);
    if (lines.length === 0) return null;
    return (
      <ul className="list-disc pl-5 space-y-1 text-sm">
        {lines.map((line, idx) => (
          <li key={idx}>{line}</li>
        ))}
      </ul>
    );
  };

  // Print helper: convert multiline text to HTML <ul> list with bullets
  const formatLinesAsList = (text?: string) => {
    if (!text) return '';
    const lines = text
      .split(/\r?\n/)
      .map(l => l.trim())
      .filter(l => l.length > 0);
    if (lines.length === 0) return '';
    const items = lines
      .map(l => `<li>${l.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</li>`) 
      .join('');
    return `<ul class=\"bullet-list\">${items}</ul>`;
  };

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setError(null);
      // Simulate loading delay
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!normalizedPrescription) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Error</DialogTitle>
            <DialogDescription>No prescription data available</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  const getDoctorRegistration = (doctorId: string) => {
    try {
      if (!doctors || !Array.isArray(doctors) || doctors.length === 0) {
        return '';
      }
      const doctor = doctors.find(d => d.id === doctorId);
      return doctor?.registrationNumber || '';
    } catch (error) {
      console.error('Error getting doctor registration:', error);
      return '';
    }
  };

  const buildPrintHtml = () =>
    buildPrescriptionPrintHtml({
      profile,
      patientName,
      patientAge,
      patientGender,
      prescription: normalizedPrescription,
      getDoctorName,
      getDoctorRegistration,
    });

  const handlePrint = () => {
    try {
      setIsLoading(true);
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        // Fallback to regular print if popup is blocked
        onPrint();
        setIsLoading(false);
        return;
      }

      printWindow.document.write(buildPrintHtml());

      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print();
        printWindow.close();
        setIsLoading(false);
      };
      
    } catch (error) {
      setError('Failed to print prescription');
      console.error('Print error:', error);
      setIsLoading(false);
    }
  };

  const handleWhatsAppShare = async () => {
    setIsSharingWhatsApp(true);
    try {
      const patientIdFormatted = formatPatientId(normalizedPrescription.patientId);
      const filename = prescriptionPdfFilename(patientIdFormatted, normalizedPrescription.createdAt);
      const result = await sharePrescriptionPdfDownloadAndWhatsApp({
        fullDocumentHtml: buildPrintHtml(),
        filename,
        patientName,
        hospitalName: profile.name,
        prescriptionDate: format(new Date(normalizedPrescription.createdAt), 'dd/MM/yyyy'),
        phoneNumber: patientContactNumber ?? patient?.contactNumber,
      });
      toast({
        title: "Prescription PDF ready",
        description: result.message,
        variant: result.success ? "default" : "destructive",
      });
    } catch (err) {
      console.error('WhatsApp PDF share failed:', err);
      toast({
        title: 'Could not create PDF',
        description: err instanceof Error ? err.message : 'Please try again or use Print.',
        variant: 'destructive',
      });
    } finally {
      setIsSharingWhatsApp(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Prescription</DialogTitle>
          <DialogDescription>
            Created on {normalizedPrescription && format(new Date(normalizedPrescription.createdAt), "dd MMM yyyy, HH:mm")}
          </DialogDescription>
        </DialogHeader>
        
        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md mb-4">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hospital-primary"></div>
          </div>
        ) : (
          <div id="prescription-print-content" className="p-4">
            <style>
              {`
                @media print {
                  body {
                    font-family: Arial, sans-serif;
                  }
                  .header {
                    text-align: center;
                    border-bottom: 2px solid #2563eb;
                    padding-bottom: 15px;
                    margin-bottom: 12px;
                  }
                  .hospital-name { font-size: 1.2rem; font-weight: 700; color: #2563eb; margin-bottom: 4px; }
                  .hospital-details { font-size: 0.7rem; color: #4b5563; }
                  .prescription-section {
                    margin-bottom: 12px;
                  }
                  .prescription-table {
                    width: 100%;
                    border-collapse: separate;
                    border-spacing: 0;
                    margin: 8px 0;
                    border-radius: 8px;
                    overflow: hidden;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                  }
                  .prescription-table th {
                    background-color: #1e40af;
                    color: white;
                    font-size: 0.75rem;
                    font-weight: 600;
                    padding: 8px 6px;
                    text-align: left;
                    border-bottom: 2px solid #1e3a8a;
                  }
                  .prescription-table td {
                    padding: 6px;
                    font-size: 0.75rem;
                    border-bottom: 1px solid #e5e7eb;
                    vertical-align: top;
                  }
                  .prescription-table tr:nth-child(even) {
                    background-color: #f8fafc;
                  }
                  .prescription-table tr:hover {
                    background-color: #f1f5f9;
                  }
                  .prescription-table tr:last-child td {
                    border-bottom: none;
                  }
                  .prescription-table .medication-name {
                    font-weight: 500;
                    color: #1e40af;
                  }
                  .prescription-table .medication-details {
                    color: #4b5563;
                  }
                  .prescription-signature {
                    margin-top: 40px;
                  }
                  .no-print {
                    display: none;
                  }
                  .hospital-details {
                    text-align: center;
                    margin-bottom: 8px;
                    padding: 8px;
                    background: linear-gradient(to right, #f8fafc, #f1f5f9);
                    border-radius: 6px;
                    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
                  }
                  .hospital-name {
                    font-size: 1.2rem;
                    font-weight: 600;
                    color: #1e40af;
                    margin-bottom: 2px;
                    line-height: 1.2;
                    letter-spacing: 0.5px;
                  }
                  .hospital-tagline {
                    font-size: 0.7rem;
                    color: #4b5563;
                    margin-bottom: 2px;
                    line-height: 1.2;
                    font-style: italic;
                  }
                  .hospital-address {
                    font-size: 0.65rem;
                    color: #6b7280;
                    margin-bottom: 2px;
                    line-height: 1.2;
                  }
                  .hospital-contact {
                    font-size: 0.65rem;
                    color: #6b7280;
                    line-height: 1.2;
                    display: flex;
                    justify-content: center;
                    gap: 12px;
                  }
                  .patient-info-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 8px;
                    margin-bottom: 12px;
                  }
                  .patient-info-section {
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    padding: 6px;
                    background-color: #f8fafc;
                  }
                  .patient-info-title {
                    font-weight: 500;
                    margin-bottom: 2px;
                    color: #1e40af;
                    font-size: 0.75rem;
                    line-height: 1.2;
                  }
                  .section-title {
                    font-size: 0.8rem;
                    font-weight: 500;
                    margin-bottom: 6px;
                    color: #1e40af;
                    line-height: 1.2;
                    padding-left: 4px;
                    border-left: 3px solid #1e40af;
                  }
                  .content-box {
                    border: none;
                    border-radius: 0;
                    padding: 0;
                    font-size: 0.75rem;
                    background-color: transparent;
                  }
                  .clinical-section {
                    margin-bottom: 8px;
                  }
                  .clinical-section:last-child {
                    margin-bottom: 0;
                  }
                  .clinical-label {
                    font-weight: 500;
                    color: #4b5563;
                    margin-bottom: 2px;
                  }
                  .clinical-content {
                    color: #1f2937;
                  }
                }
              `}
            </style>

            <div className="space-y-3">

              {/* Header */}
              {profile.usePrescriptionHeader !== false && (
              <div className="header">
                <div className="hospital-name">{profile.name}</div>
                <div className="hospital-details">
                  {profile.address}<br />
                  Phone: {profile.phone1} | Email: {profile.email}<br />
                  {profile.website}
                </div>
              </div>
              )}
              
                            {/* Patient Information */}
              <div className="patient-info-grid">
                <div className="patient-info-section">
                  <div className="patient-info-title">Patient Details</div>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-semibold">Name:</span> {patientName}</p>
                    <p><span className="font-semibold">Age:</span> {patientAge} years</p>
                    <p><span className="font-semibold">Gender:</span> {patientGender}</p>
                    <p><span className="font-semibold">ID:</span> {formatPatientId(normalizedPrescription.patientId)}</p>
                  </div>
                </div>
                <div className="patient-info-section">
                  <div className="patient-info-title">Visit Details</div>
                  <div className="space-y-1 text-sm">
                    <p><span className="font-semibold">Date:</span> {format(new Date(normalizedPrescription.createdAt), "dd MMM yyyy")}</p>
                    <p><span className="font-semibold">Prescription ID:</span> {normalizedPrescription.id.slice(0, 8)}</p>
                  </div>
                </div>
              </div>
              
              {/* Chief Complaints & Clinical Findings Row */}
              {(normalizedPrescription.chief_complaints || normalizedPrescription.clinical_findings) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {normalizedPrescription.chief_complaints && (
                    <div className="prescription-section">
                      <div className="section-title">Chief Complaints</div>
                      <div className="content-box">{renderBulletLines(normalizedPrescription.chief_complaints)}</div>
                    </div>
                  )}
                  
                  {normalizedPrescription.clinical_findings && (
                    <div className="prescription-section">
                      <div className="section-title">Clinical Findings</div>
                      <div className="content-box">{renderBulletLines(normalizedPrescription.clinical_findings)}</div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Duration of Complaints */}
              {normalizedPrescription.complaints_duration && (
                <div className="prescription-section">
                  <div className="section-title">Duration of Complaints</div>
                  <div className="content-box">{normalizedPrescription.complaints_duration}</div>
                </div>
              )}
              
              {/* Lab Tests & Diagnosis Row */}
              {(normalizedPrescription.diagnosis || (normalizedPrescription.lab_tests && normalizedPrescription.lab_tests.length > 0)) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {normalizedPrescription.lab_tests && normalizedPrescription.lab_tests.length > 0 && (
                    <div className="prescription-section">
                      <div className="section-title">Laboratory Tests</div>
                      <div className="content-box">
                        {Array.isArray(normalizedPrescription.lab_tests) 
                          ? normalizedPrescription.lab_tests.map((test, index) => (
                              <div key={index} className="mb-1">
                                {index + 1}. {test}
                              </div>
                            ))
                          : normalizedPrescription.lab_tests
                        }
                      </div>
                    </div>
                  )}
                  
                  {normalizedPrescription.diagnosis && (
                    <div className="prescription-section">
                      <div className="section-title">Diagnosis</div>
                      <div className="content-box">{normalizedPrescription.diagnosis}</div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Medications */}
              <div className="prescription-section">
                <div className="section-title">Medications</div>
                {normalizedPrescription.medications.length > 0 ? (
                  <div className="content-box">
                    <Table className="prescription-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[25%]">Medication</TableHead>
                          <TableHead className="w-[15%]">Dosage</TableHead>
                          <TableHead className="w-[25%]">Frequency</TableHead>
                          <TableHead className="w-[20%]">Duration</TableHead>
                          <TableHead className="w-[15%]">Instructions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {normalizedPrescription.medications.map((med, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="medication-name">{med.name}</TableCell>
                            <TableCell className="medication-details">{med.dosage}</TableCell>
                            <TableCell className="medication-details">
                              <div className="space-y-1">
                                <div>{med.frequency}</div>
                                {med.frequencyPattern && med.frequencyPattern !== "custom" && med.frequencyPattern !== "" && (
                                  <div className="text-xs text-muted-foreground">
                                    Pattern: {med.frequencyPattern}
                                  </div>
                                )}
                                {med.frequencyDescription && med.frequencyDescription !== "" && med.frequencyPattern !== "custom" && (
                                  <div className="text-xs text-muted-foreground">
                                    {med.frequencyDescription}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="medication-details">{formatMedicationDuration(med.duration)}</TableCell>
                            <TableCell className="medication-details text-xs">
                              {med.instructions || "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No medications prescribed</p>
                )}
              </div>
              
              {/* Additional Notes */}
              {normalizedPrescription.notes && (
                <div className="prescription-section">
                  <div className="section-title">Additional Notes</div>
                  <div className="content-box">{renderBulletLines(normalizedPrescription.notes)}</div>
                </div>
              )}
              
              {/* Instructions */}
              {normalizedPrescription.instructions && (
                <div className="prescription-section">
                  <div className="section-title">Instructions</div>
                  <div className="content-box">{normalizedPrescription.instructions}</div>
                </div>
              )}
              
              {/* Follow-up Date */}
              {normalizedPrescription.follow_up_date && (
                <div className="prescription-section">
                  <div className="flex items-center gap-2">
                    <span className="section-title mb-0">Follow-up Date:</span>
                    <span className="font-bold">{format(new Date(normalizedPrescription.follow_up_date), "dd MMM yyyy")}</span>
                  </div>
                </div>
              )}
              
              {/* Signature */}
              <div className="prescription-signature">
                <div className="flex justify-end">
                  <div className="text-right">
                    <div className="w-48 border-t border-black mb-1 ml-auto" />
                    <p className="font-bold text-sm">Dr. {getDoctorName(normalizedPrescription.doctorId)}</p>
                    {getDoctorRegistration(normalizedPrescription.doctorId) && (
                      <p className="text-xs text-muted-foreground">Reg. No: {getDoctorRegistration(normalizedPrescription.doctorId)}</p>
                    )}
                  </div>
                </div>
              </div>

              {profile.usePrescriptionHeader !== false && (
                <div className="mt-8 text-center text-sm text-foreground border-t border-muted pt-2">
                  <span className="font-semibold">For Appointments:</span> {profile.phone1 || '-'}
                  {' '}| <span className="font-semibold">Alt:</span> {profile.phone2 || '-'}
                  {' '}| <span className="font-semibold">Emergency contact:</span> {profile.phone3 || '-'}
                </div>
              )}
            </div>
          </div>
        )}
        
        <DialogFooter className="no-print flex flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={handlePrint}
            disabled={isLoading || isSharingWhatsApp}
          >
            <Printer className="h-4 w-4 mr-2" />
            {isLoading ? 'Printing...' : 'Print Prescription'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className={cn(whatsAppButtonClassName)}
            onClick={() => void handleWhatsAppShare()}
            disabled={isLoading || isSharingWhatsApp}
          >
            {isSharingWhatsApp ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <MessageCircle className="h-4 w-4 mr-2" />
            )}
            {isSharingWhatsApp ? 'Creating PDF...' : 'WhatsApp PDF'}
          </Button>
          <DialogClose asChild>
            <Button variant="secondary" disabled={isLoading || isSharingWhatsApp}>
              <X className="h-4 w-4 mr-2" />
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

